import {BusinessOutlined, Search} from '@mui/icons-material';
import {Box, debounce, OutlinedInput} from '@mui/material';
import {ChangeEvent, FC, useMemo, useState} from 'react';
import {useIntl} from 'react-intl';
import {useNavigate} from 'react-router-dom';
import {businessesApi} from 'src/api';
import {useAppSelector} from 'src/app/hooks';
import AMPModal from 'src/components/common/AMPModal';
import {PageHeader} from 'src/components/common/PageHeader';
import {CardList} from 'src/components/common/CardList';
import {ResponsiveTable} from 'src/components/common/ResponsiveTable';
import {BusinessForm} from 'src/components/forms/BusinessForm';
import {EmptyTable} from 'src/components/tables/EmptyTable';
import {Table} from 'src/components/tables/Table';
import {TableLayout} from 'src/components/tables/TableLayout';
import {searchInputSx, toolbarActionsSx} from 'src/components/tables/tableToolbar.sx';
import {useCan} from 'src/permissions/hooks';
import {AppRouteEnum, AppRouteSpace} from 'src/utils/helper';
import {columnConfigBusinesses} from './Businesses.tableConfig';
import {BusinessItemCard} from './BusinessItemCard';

/**
 * Every business on the platform.
 *
 * Only somebody who works across tenants ever needs it, which is why the route is guarded by the
 * permission that lists them. Built from the same PageHeader / TableLayout / Table as every other
 * list in the application, so it sits at the same width, spacing and row height as the ones beside
 * it in the sidebar - a hand-rolled list looked close enough on its own and wrong next to them.
 *
 * Opening a row goes to that business's own page rather than to the user screen it used to borrow:
 * a business is not a person, and the borrowed screen answered a different question.
 */
export const Businesses: FC = () => {
    const intl = useIntl();
    const navigate = useNavigate();
    const activeBusiness = useAppSelector((state) => state.common.activeBusiness);
    // The same permission the registration endpoint itself checks, so the button appears exactly
    // when pressing it would work.
    const canCreateBusiness = useCan('registration.createBusiness');

    const [open, setOpen] = useState(false);
    const [keyword, setKeyword] = useState('');
    const [tableParams, setTableParams] = useState({page: 0, size: 100});
    const {data, isFetching, refetch} = businessesApi.useGetBusinessesQuery({params: tableParams});
    const columns = useMemo(() => columnConfigBusinesses(activeBusiness), [activeBusiness]);

    /**
     * Filtered here rather than by the server: `GET /businesses` takes a page and nothing else, so
     * there is no keyword to send. It searches what has been loaded - the name, and the address as
     * it is written in the column - which is the whole list at any realistic number of businesses.
     */
    const rows = useMemo(() => {
        const all = data?.content ?? [];
        const needle = keyword.trim().toLowerCase();
        if (!needle) {
            return all;
        }
        return all.filter((business) => {
            const address = business.address;
            const haystack = [
                business.name,
                business.uuid,
                address?.street,
                address?.city,
                address?.country,
            ]
                .filter(Boolean)
                .join(' ')
                .toLowerCase();
            return haystack.includes(needle);
        });
    }, [data?.content, keyword]);

    const handleChangePage = (page: number) => {
        setTableParams({...tableParams, page});
    };

    const handleChangeRowsNumber = (event: ChangeEvent<HTMLInputElement>) => {
        setTableParams({page: 0, size: +event.target.value || 100});
    };

    const handleSearch = debounce((value: string) => setKeyword(value), 400);

    const handleCloseModal = () => {
        setOpen(false);
        // The form posts through the registration slice, which knows nothing about this list's
        // cache, so the new business would not appear until something else refetched it.
        void refetch();
    };

    const table = (
        <Table
            rows={rows}
            columns={columns}
            loading={isFetching}
            hideFooter
            checkboxSelection={false}
            onRowClick={(params) =>
                navigate(AppRouteSpace([
                    AppRouteEnum.app,
                    AppRouteEnum.businesses,
                    String(params.row.id),
                ]))
            }
            sx={{'& .MuiDataGrid-row': {cursor: 'pointer'}}}
        />
    );

    return (
        <Box>
            <PageHeader
                title={intl.formatMessage({id: 'businesses'})}
                icon={<BusinessOutlined/>}
                onlyTitle={!canCreateBusiness}
                // PageHeader translates the label itself, so this is the key, not the text.
                buttonText="addBusiness"
                onClick={() => setOpen(true)}
            >
                <Box sx={toolbarActionsSx}>
                    <OutlinedInput
                        sx={searchInputSx}
                        color="secondary"
                        type="text"
                        placeholder={intl.formatMessage({id: 'search'})}
                        name="search"
                        onChange={(event) => handleSearch(event.target.value)}
                        startAdornment={<Search/>}
                    />
                </Box>
            </PageHeader>

            <TableLayout
                totalPages={data?.page?.totalPages ?? 0}
                page={data?.page?.number ?? tableParams.page}
                pageSize={tableParams.size}
                onPageChange={handleChangePage}
                onPageSizeChange={handleChangeRowsNumber}
            >
                <ResponsiveTable
                    desktopElement={table}
                    responsiveElement={
                        rows.length ? (
                            <CardList>
                                {rows.map((business) => (
                                    <BusinessItemCard
                                        key={business.id}
                                        business={business}
                                        activeId={activeBusiness}
                                    />
                                ))}
                            </CardList>
                        ) : (
                            // What the grid puts in its no-rows overlay, said the same way here.
                            <EmptyTable/>
                        )
                    }
                />
            </TableLayout>

            <AMPModal
                handleClose={handleCloseModal}
                open={open}
                title={intl.formatMessage({id: 'addBusiness'})}
            >
                <BusinessForm handleClose={handleCloseModal}/>
            </AMPModal>
        </Box>
    );
};
